import { ref, computed } from 'vue';
import { productTypes } from '@/Models.js';
import { cloneDeep } from '@/Utils';
import { findDuplicateSetItems, restoreOriginalProduct } from '@/Utils/ProductUtility.js';

// setup() runs before data(), so callers pass the top-level quantity in rather than the
// engine reading this.model; product price recalculation stays with the consumer.
export const useProductSplit = () => {
    const productData = ref({});
    const originalProductData = ref({});
    const extraSetItems = ref([]);
    const maxQuantity = ref(null);

    // Stable per-row id so the v-for key survives splicing; an index key would remount
    // trailing rows and drop their input state.
    let uidCounter = 0;
    const nextUid = () => (uidCounter += 1);
    const assignUids = (items) => {
        (items || []).forEach((item) => {
            item.uid = nextUid();
        });
    };

    const isProductPiece = product => product.type !== productTypes.set;

    const productSetItems = computed(() => {
        if (productData.value && !isProductPiece(productData.value)) {
            return productData.value.productSetItems.filter(s => s.setItem.options.length > 0);
        }

        return [];
    });

    const setDefaultOptionValues = () => {
        if (productData.value) {
            for (const option of productData.value.options) {
                if (!option.productOptionValueID) {
                    option.productOptionValueID = option.defaultProductOptionValueID;
                }
            }
            for (const item of productData.value.productSetItems) {
                for (const option of item.setItem.options) {
                    if (!option.productOptionValueID) {
                        option.productOptionValueID = option.defaultProductOptionValueID;
                    }
                }
            }
        }
    };

    const canProductBeRemoved = productSetItem => extraSetItems.value.includes(productSetItem);

    const canProductBeSplit = productSetItem =>
        productSetItem.quantity > 1
        && productSetItem.setItem.options.length > 0
        && !canProductBeRemoved(productSetItem);

    const getSourceItem = (productSetItem, data) =>
        data.productSetItems.find(p => p.setItemID === productSetItem.setItemID);

    const getTotalSetItemQuantity = productSetItem =>
        productData.value
            ? productData.value.productSetItems.filter(i => i.setItemID === productSetItem.setItemID)
                .map(i => i.quantity)
                .reduce((a, b) => a + b, 0)
            : 0;

    const getTotalSplitItemsQuantity = productSetItem =>
        extraSetItems.value.filter(i => i.setItemID === productSetItem.setItemID)
            .map(i => i.quantity)
            .reduce((a, b) => a + b, 0);

    const getMaxQuantity = (productSetItem, quantity) =>
        getSourceItem(productSetItem, originalProductData.value).quantity * quantity;

    const getAvailableQuantity = (productSetItem, quantity) =>
        getMaxQuantity(productSetItem, quantity) - getTotalSplitItemsQuantity(productSetItem) - 1;

    const onQuantityChanged = (productSetItem, newQuantity, quantity) => {
        const qty = Number(newQuantity);
        const difference = qty - productSetItem.quantity;
        productSetItem.quantity = qty;
        const sourceItem = getSourceItem(productSetItem, productData.value);
        if (getAvailableQuantity(productSetItem, quantity) < difference) {
            sourceItem.quantity = 1;
        }
        else if (difference < 0 && getMaxQuantity(productSetItem, quantity) < sourceItem.quantity - difference) {
            sourceItem.quantity = getMaxQuantity(productSetItem, quantity) - getTotalSplitItemsQuantity(productSetItem);
        }
        else {
            sourceItem.quantity -= difference;
        }
    };

    const recalculateSetItemQuantities = (currentQuantity) => {
        for (const setItem of originalProductData.value.productSetItems) {
            const requiredQty = setItem.quantity / originalProductData.value.quantity * currentQuantity;
            let difference = requiredQty - getTotalSetItemQuantity(setItem);
            const sourceItem = getSourceItem(setItem, productData.value);

            let extraItem;
            if (difference > 0) {
                for (extraItem in productData.value.productSetItems.filter(i => i.setItemID === setItem.setItemID && i.quantity === 0)) {
                    extraItem.quantity = 1;
                    difference--;
                    if (difference === 0) {
                        break;
                    }
                }

                sourceItem.quantity += difference;
            }
            else if (difference < 0) {
                if (sourceItem.quantity + difference > 0) {
                    sourceItem.quantity += difference;
                }
                else {
                    difference += sourceItem.quantity - 1;
                    sourceItem.quantity = 1;

                    for (extraItem in productData.value.productSetItems.filter(i => i.setItemID === setItem.setItemID && i.quantity === 0)) {
                        extraItem.quantity = 0;
                        difference++;
                        if (difference === 0) {
                            break;
                        }
                    }
                }
            }
        }
    };

    const splitProductItem = (productSetItem) => {
        const index = productData.value.productSetItems.indexOf(productSetItem);
        const newItem = cloneDeep(productSetItem);
        newItem.quantity = 1;
        newItem.orderItemID = 0;
        newItem.uid = nextUid();
        productSetItem.quantity -= 1;
        productData.value.productSetItems.splice(index + 1, 0, newItem);
        extraSetItems.value.push(newItem);

        return newItem;
    };

    // A split may take at most all-but-one copy of the set item (the original keeps ≥1).
    const getSplitLimit = (productSetItem, quantity) => {
        const otherSplits = extraSetItems.value
            .filter(i => i.setItemID === productSetItem.setItemID && i !== productSetItem)
            .reduce((sum, i) => sum + i.quantity, 0);

        return getMaxQuantity(productSetItem, quantity) - otherSplits - 1;
    };

    const applySplitAmount = (productSetItem, amount, quantity) => {
        const source = getSourceItem(productSetItem, productData.value);
        const otherSplits = extraSetItems.value
            .filter(i => i.setItemID === productSetItem.setItemID && i !== productSetItem)
            .reduce((sum, i) => sum + i.quantity, 0);
        const total = getMaxQuantity(productSetItem, quantity);
        const limit = total - otherSplits - 1;
        const value = Number(amount);
        const isValid = amount !== null && amount !== '' && Number.isInteger(value) && value >= 1 && value <= limit;
        productSetItem.quantity = isValid ? value : 0;
        source.quantity = total - otherSplits - productSetItem.quantity;
    };

    const beginSplit = (setItemID, quantity) => {
        const source = productData.value.productSetItems.find(
            i => i.setItemID === setItemID && !canProductBeRemoved(i));
        const newItem = cloneDeep(source);
        newItem.quantity = 0;
        newItem.orderItemID = 0;
        newItem.shouldFocus = true;
        newItem.uid = nextUid();
        const index = productData.value.productSetItems.findLastIndex(i => i.setItemID === setItemID);
        productData.value.productSetItems.splice(index + 1, 0, newItem);
        extraSetItems.value.push(newItem);
        applySplitAmount(newItem, 1, quantity);

        return newItem;
    };

    // applySplitAmount parks an invalid amount at quantity 0, which blocks confirmation.
    const hasInvalidSplits = () => extraSetItems.value.some(i => i.quantity < 1);

    const removeProductItem = (productSetItem, quantity) => {
        const rewardSetItem = originalProductData.value.productSetItems.find(i => i.setItemID === productSetItem.setItemID);
        const originalSetItem = productData.value.productSetItems.find(i => i.setItemID === productSetItem.setItemID);
        if (rewardSetItem && originalSetItem) {
            extraSetItems.value.splice(extraSetItems.value.indexOf(productSetItem), 1);
            productData.value.productSetItems.splice(productData.value.productSetItems.indexOf(productSetItem), 1);
            recalculateSetItemQuantities(quantity);
        }
    };

    const resetSplitItems = (quantity) => {
        extraSetItems.value = [];
        productData.value.productSetItems = originalProductData.value.productSetItems.map(i => cloneDeep(i));
        assignUids(productData.value.productSetItems);
        setDefaultOptionValues();
        recalculateSetItemQuantities(quantity);
    };

    const prepareProductData = (data, quantity) => {
        extraSetItems.value = [];
        productData.value = cloneDeep(data);
        originalProductData.value = restoreOriginalProduct(data);
        findDuplicateSetItems(productData.value.productSetItems, extraSetItems.value);
        assignUids(productData.value.productSetItems);
        setDefaultOptionValues();
        recalculateSetItemQuantities(quantity);
        maxQuantity.value = productData.value.maxQuantity;

        if (productData.value.productSetItems) {
            for (const item of productData.value.productSetItems) {
                if (item.setItem.maxQuantity !== null) {
                    maxQuantity.value = Math.min(maxQuantity.value || 9999,
                        Math.floor(item.setItem.maxQuantity / item.quantity));
                }
            }
        }
    };

    const clearProductData = () => {
        productData.value = null;
        extraSetItems.value = [];
        maxQuantity.value = null;
    };

    const quantityIsExceeded = (quantity) => {
        if (maxQuantity.value !== null && maxQuantity.value < quantity) {
            return false;
        }

        return true;
    };

    return {
        productData,
        originalProductData,
        extraSetItems,
        maxQuantity,
        productSetItems,
        isProductPiece,
        setDefaultOptionValues,
        canProductBeRemoved,
        canProductBeSplit,
        getSourceItem,
        getTotalSetItemQuantity,
        getTotalSplitItemsQuantity,
        getMaxQuantity,
        getAvailableQuantity,
        onQuantityChanged,
        recalculateSetItemQuantities,
        splitProductItem,
        getSplitLimit,
        applySplitAmount,
        beginSplit,
        hasInvalidSplits,
        removeProductItem,
        resetSplitItems,
        prepareProductData,
        clearProductData,
        quantityIsExceeded,
    };
};
